/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Header from '../../components/layout/Header';
import VerdictBadge from '../../components/finops/VerdictBadge';
import { finopsService } from '../../services/finopsApi';
import { formatMicroUSD, savingsColorClass } from '../../components/finops/finopsFormat';
import { useFinopsPermissions } from '../../hooks/useFinopsPermissions';
import type { RecommendationSource } from '../../types/finops';

const SOURCE_LABEL: Record<RecommendationSource, string> = {
  agent: 'AI analyst',
  deterministic: 'Calculated',
};

// Analysis runs asynchronously in the background (POST /analyze returns 202
// immediately). Rather than poll, we refetch once shortly after kicking it
// off - a manual "Refresh analysis" click always re-runs both steps too.
const ANALYSIS_REFETCH_DELAY_MS = 6000;

const Recommendations: React.FC = () => {
  const [connectionId, setConnectionId] = useState<string>('');
  const queryClient = useQueryClient();
  const { isOperator } = useFinopsPermissions();

  const { data: connections } = useQuery({
    queryKey: ['finops-connections'],
    queryFn: () => finopsService.listConnections().then((res) => res.data.connections),
  });

  useEffect(() => {
    if (!connectionId && connections && connections.length > 0) {
      setConnectionId(connections[0].id);
    }
  }, [connections, connectionId]);

  const { data: recommendations, isLoading, isError, refetch } = useQuery({
    queryKey: ['finops-recommendations', connectionId],
    queryFn: () => finopsService.listRecommendations(connectionId).then((res) => res.data.recommendations),
    enabled: !!connectionId,
  });

  const analyzeMutation = useMutation({
    mutationFn: () => finopsService.analyze(connectionId),
    onSuccess: () => {
      toast.success('Analysis started - refreshing shortly');
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['finops-recommendations', connectionId] });
      }, ANALYSIS_REFETCH_DELAY_MS);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Analysis already in progress'),
  });

  return (
    <div>
      <Header title="Recommendations" subtitle="Migration verdicts computed from your AWS inventory, costs and credit burn" />

      <div className="p-6 space-y-6">
        {!connections || connections.length === 0 ? (
          <div className="card">
            <div className="text-center py-12">
              <h3 className="text-sm font-medium text-white">No AWS connections yet</h3>
              <p className="mt-1 text-sm text-gray-400 max-w-md mx-auto">
                Connect an AWS account and run Analyze to see recommendations here.
              </p>
              <Link to="/finops/connections" className="btn-primary inline-flex items-center gap-2 mt-4">
                Connect AWS account
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div className="max-w-xs flex-1">
                <label htmlFor="finops-rec-connection" className="block text-sm font-medium text-gray-300 mb-2">
                  Connection
                </label>
                <select
                  id="finops-rec-connection"
                  className="input"
                  value={connectionId}
                  onChange={(e) => setConnectionId(e.target.value)}
                >
                  {connections.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.status})
                    </option>
                  ))}
                </select>
              </div>
              {isOperator && (
                <button
                  type="button"
                  onClick={() => analyzeMutation.mutate()}
                  disabled={!connectionId || analyzeMutation.isPending}
                  className="btn-secondary inline-flex items-center gap-1.5"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  {analyzeMutation.isPending ? 'Refreshing...' : 'Refresh analysis'}
                </button>
              )}
            </div>

            {isLoading ? (
              <div className="card">
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4" />
                  <p className="text-sm text-gray-400">Loading recommendations...</p>
                </div>
              </div>
            ) : isError ? (
              <div className="card">
                <div className="text-center py-12">
                  <p className="text-red-400 mb-4">Failed to load recommendations</p>
                  <button onClick={() => refetch()} className="btn-primary">
                    Retry
                  </button>
                </div>
              </div>
            ) : !recommendations || recommendations.length === 0 ? (
              <div className="card">
                <div className="text-center py-12">
                  <h3 className="text-sm font-medium text-white">No recommendations yet</h3>
                  <p className="mt-1 text-sm text-gray-400 max-w-md mx-auto">
                    Sync inventory and costs from the Connections page, then click Refresh analysis.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {recommendations.map((rec) => (
                  <div key={rec.id} className="card">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <VerdictBadge verdict={rec.verdict} migrateAt={rec.migrate_at} />
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-800 text-gray-400 border border-gray-700">
                            {SOURCE_LABEL[rec.source]}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 mt-2">{rec.explanation}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          AWS {formatMicroUSD(rec.aws_monthly_micro_usd)}/mo → Podstack{' '}
                          {formatMicroUSD(rec.podstack_monthly_micro_usd)}/mo
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-lg font-semibold ${savingsColorClass(rec.monthly_savings_micro_usd)}`}>
                          {formatMicroUSD(rec.monthly_savings_micro_usd)}/mo
                        </p>
                        <p className="text-xs text-gray-500">
                          {rec.monthly_savings_micro_usd < 0 ? 'costs more on Podstack' : 'savings'}
                        </p>
                        <button
                          type="button"
                          disabled
                          title="Plan migration is coming in Phase 2"
                          className="btn-secondary btn-sm mt-3 opacity-50 cursor-not-allowed"
                        >
                          Plan migration
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Recommendations;
